import { useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DataframeSplitData } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";

interface DataTableProps {
  data: DataframeSplitData;
  pageSize?: number;
  /** Total rows in the full dataset (before truncation by the backend). */
  totalDataRows?: number;
  /** Number of rows the backend sent down (the truncated preview). */
  displayedRows?: number;
}
// NOTE: The Analysis Agent orchestrator has a separate configurable setting
// (artifact_preview_max_rows) that also defaults to 10 to match this pagination.
// If you change this value, consider updating the backend setting to keep them in sync.
// See: product/ai-agents/analysis-agent/src/analysis_agent/settings.py

const DEFAULT_PAGE_SIZE = 10;

/**
 * DataTable component for rendering dataframe data with sorting, filtering, and pagination.
 * Accepts data in pandas "split" orient format (columns, index, data arrays).
 */
export function DataTable({
  data,
  pageSize = DEFAULT_PAGE_SIZE,
  totalDataRows,
  displayedRows,
}: DataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  // Transform pandas "split" format data into row objects for TanStack Table
  const tableData = useMemo(() => {
    return data.data.map((row) => {
      const rowObj: Record<string, unknown> = {};
      data.columns.forEach((col, idx) => {
        rowObj[col] = row[idx];
      });
      return rowObj;
    });
  }, [data]);

  // Generate column definitions from column names
  const columns: ColumnDef<Record<string, unknown>>[] = useMemo(() => {
    return data.columns.map((columnName, index) => ({
      // Use accessorFn + explicit id instead of accessorKey to avoid two TanStack Table pitfalls:
      // 1. accessorKey: "" crashes when column names are empty strings (e.g. pivot table index column)
      // 2. accessorKey treats dots as nested path separators, breaking columns like "a.b"
      id: columnName || `__col_${index}__`,
      accessorFn: (row: Record<string, unknown>) => row[columnName],
      header: ({ column }) => {
        const isSorted = column.getIsSorted();
        return (
          <button
            type="button"
            className={cn(
              "flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer select-none",
              isSorted ? "text-foreground" : "text-muted-foreground"
            )}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            aria-label={`Sort by ${columnName}`}
          >
            {columnName}
            {isSorted === "asc" ? (
              <ArrowUp className="size-3" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="size-3" />
            ) : (
              <ArrowUpDown className="size-3 opacity-50" />
            )}
          </button>
        );
      },
      cell: ({ getValue }) => {
        const value = getValue();
        // Handle null/undefined values
        if (value === null || value === undefined) {
          return <span className="text-muted-foreground italic">null</span>;
        }
        // Format numbers with reasonable precision
        if (typeof value === "number") {
          return Number.isInteger(value) ? value : value.toFixed(4);
        }
        return String(value);
      },
    }));
  }, [data.columns]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  // The number of rows the backend actually sent (truncated preview)
  const previewRowCount = displayedRows ?? filteredRowCount;

  return (
    <div className="space-y-3">
      {/* Filter Input */}
      <div className="flex items-center gap-2">
        <Input
          placeholder="Filter all columns..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="h-8 w-64 text-xs focus-visible:ring-0"
          aria-label="Filter table"
        />
        {globalFilter && (
          <span className="text-xs text-muted-foreground">
            {filteredRowCount} result{filteredRowCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Table */}
      <Table tableContainerProps={{ className: "border rounded-md" }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="text-xs">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="text-xs">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-16 text-center text-muted-foreground"
              >
                No results found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {totalDataRows && totalDataRows > previewRowCount ? (
              <>
                Previewing {table.getState().pagination.pageIndex * pageSize + 1}
                &ndash;
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * pageSize,
                  filteredRowCount
                )}{" "}
                of {previewRowCount} (from {totalDataRows.toLocaleString()} total results)
              </>
            ) : (
              <>
                {table.getState().pagination.pageIndex * pageSize + 1}
                &ndash;
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * pageSize,
                  filteredRowCount
                )}{" "}
                of {filteredRowCount}
              </>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
              className="h-7 has-[>svg]:px-1.5"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
              className="h-7 has-[>svg]:px-1.5"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
